import { IsDateString, IsOptional, IsString, MaxLength } from 'class-validator';
import type { SensorFirmwareEntity } from '../entity';

/**
 * Multipart form fields for creating a new sensor firmware version. Sent
 * alongside the firmware binary in the `file` part of the request; the server
 * uploads the binary to S3 and links it, so the client never supplies a file
 * id. `fileName` is optional — it defaults to the uploaded file's name and is
 * the exact name the device requests on the download endpoint.
 */
export class CreateSensorFirmwareFormDto {
	@IsString()
	@MaxLength(64)
	public version!: string;

	@IsOptional()
	@IsString()
	@MaxLength(255)
	public fileName?: string;

	@IsDateString()
	public effectiveDate!: string;

	@IsOptional()
	@IsString()
	@MaxLength(255)
	public notes?: string;
}

/**
 * Request body for editing an existing sensor firmware version. All fields are
 * optional; only the ones provided are changed.
 */
export class UpdateSensorFirmwareDto {
	@IsOptional()
	@IsString()
	@MaxLength(64)
	public version?: string;

	@IsOptional()
	@IsString()
	@MaxLength(255)
	public fileName?: string;

	@IsOptional()
	@IsDateString()
	public effectiveDate?: string;

	@IsOptional()
	@IsString()
	@MaxLength(255)
	public notes?: string;
}

/**
 * Row shape returned to the admin list/editor. Derived from the entity so the
 * field types can never drift from the source of truth.
 */
export type SensorFirmwareSummary = Pick<
	SensorFirmwareEntity,
	| 'id'
	| 'version'
	| 'fileName'
	| 'effectiveDate'
	| 'notes'
	| 'fkFileId'
	| 'createdDate'
	| 'modifiedDate'
>;

/**
 * The exact wire format the Meskernel sensor host expects from
 * `GET /api/sensors/ota/version`. PascalCase keys and STRING values are
 * mandated by their firmware's parser — do not change the casing or types.
 *
 * `UpdateNow`: "true" = an update may be available (host then compares the
 * version embedded in `FileName` against its own and upgrades only if newer);
 * "false" = host performs no further check.
 */
export interface OtaVersionResponseModel {
	UpdateNow: 'true' | 'false';
	FileName: string;
}
